/**
 * ComponentMarketplaceDialog — Browse and purchase ship components.
 *
 * Side-by-side layout: component list (left) | detail panel (right).
 * Buy flow: select component → "Buy & Equip" → pick ship → see before/after stats → confirm.
 * Equip flow: if you own an unequipped instance, you can equip it without buying.
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import { EventBus } from '../../utils/EventBus';
import type { MMOMonetizationManager } from '../../managers/MMOMonetizationManager';
import type { MMOStateManager, MMOUnit } from '../../managers/MMOStateManager';
import type { ComponentDefinition, ComponentInstance, ComponentCategory } from '../../shared/common/MonetizationTypes';
import { Colors, CSSColors, Fonts, FontSize, Radius, createDialogChrome, closeDialog } from './GameUITheme';
import { ShipStoreDialog } from './ShipStoreDialog';
import { ComponentEnhancementDialog } from './ComponentEnhancementDialog';
import { PaymentSelectionPanel } from './PaymentSelectionPanel';
import { PaymentFlowManager } from '../../managers/PaymentFlowManager';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

const PAD = 10;

const CATEGORIES: { id: ComponentCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'ALL' },
  { id: 'weapon', label: 'WEAP' },
  { id: 'engine', label: 'ENG' },
  { id: 'shield', label: 'SHLD' },
  { id: 'armor', label: 'ARMR' },
  { id: 'sensor', label: 'SNSR' },
  { id: 'stealth', label: 'STLH' },
  { id: 'reactor', label: 'RCTR' },
  { id: 'special', label: 'SPEC' },
];

const TIER_LABELS: Record<string, string> = {
  mk1: 'Mk I', mk2: 'Mk II', mk3: 'Mk III', mk4: 'Mk IV', prototype: 'Proto',
};
const TIER_COLORS: Record<string, string> = {
  mk1: '#aaaaaa', mk2: '#55cc55', mk3: '#5599ff', mk4: '#cc55ff', prototype: '#ffcc00',
};
const TIER_BG: Record<string, number> = {
  mk1: 0x333344, mk2: 0x223322, mk3: 0x222244, mk4: 0x332244, prototype: 0x333322,
};
const TIER_DESCRIPTIONS: Record<string, string> = {
  mk1: 'Basic', mk2: 'Improved', mk3: 'Advanced', mk4: 'Elite', prototype: 'Experimental',
};
const TIER_SORT: Record<string, number> = {
  mk1: 0, mk2: 1, mk3: 2, mk4: 3, prototype: 4,
};

/** Map component stat keys to ship-readable labels */
const STAT_LABELS: Record<string, string> = {
  hp: 'Hull HP', shields: 'Shields', armor: 'Armor', speed: 'Speed',
  evasion: 'Evasion', range: 'Sensor Range', accuracy: 'Accuracy',
  attack: 'Attack', defense: 'Defense', power: 'Power Output',
  cooldownReduction: 'Cooldown Reduc.', regenRate: 'Shield Regen',
  damage: 'Damage', fireRate: 'Fire Rate',
};

/** Map component stat keys to MMOUnit property names for before/after */
const STAT_TO_UNIT: Record<string, keyof MMOUnit> = {
  hp: 'maxHp', shields: 'maxShields', speed: 'speed',
  range: 'attackRange', attack: 'attack', defense: 'defense',
};

type DetailView = 'info' | 'buy-confirm' | 'equip-confirm';

// Scroll constants
const LIST_TOP = 90;

/** Owned component info per definition */
interface OwnedInfo {
  count: number;
  instances: ComponentInstance[];
  equippedShipIds: string[];
  unequippedIds: string[];
}

export class ComponentMarketplaceDialog extends Phaser.GameObjects.Container {
  private monetization: MMOMonetizationManager;
  private mmoState: MMOStateManager;
  private components: ComponentDefinition[] = [];
  private filteredComponents: ComponentDefinition[] = [];
  private ownedMap: Map<string, OwnedInfo> = new Map(); // definitionId => OwnedInfo
  private selectedCategory: ComponentCategory | 'all' = 'all';
  private selectedComponent: ComponentDefinition | null = null;
  private selectedShipId: string | null = null;
  private detailView: DetailView = 'info';
  private isPurchasing = false;
  private pendingComponentActionId: string | null = null;
  private enhancementDialog: ComponentEnhancementDialog | null = null;
  private paymentPanel: PaymentSelectionPanel | null = null;

  private catContainer: Phaser.GameObjects.Container;
  private listContainer: Phaser.GameObjects.Container;
  private detailContainer: Phaser.GameObjects.Container;
  private statusText: Phaser.GameObjects.Text;

  // Scrolling (list panel)
  private scrollY = 0;
  private maxScrollY = 0;
  private scrollMask: Phaser.Display.Masks.GeometryMask | null = null;
  // Scrolling (detail panel)
  private detailScrollY = 0;
  private detailMaxScrollY = 0;
  private detailScrollMask: Phaser.Display.Masks.GeometryMask | null = null;
  // Touch-drag scrolling state
  private isDraggingScroll = false;
  private dragStartY = 0;
  private dragStartScrollY = 0;

  // Responsive sizing
  private mobile: boolean;
  private dw: number;
  private listW: number;
  private detailW: number;
  private scrollVisibleH: number;
  private cardNameMaxW: number;

  private initialComponentId: string | null = null;
  private initialShipId: string | null = null;

  constructor(scene: Phaser.Scene, initialCategory?: ComponentCategory, initialComponentId?: string, initialShipId?: string) {
    const mobile = isMobile(scene);
    const DIALOG_W = mobile ? 340 : 652;
    const DIALOG_H = mobile ? 540 : 620;
    const LIST_W   = mobile ? DIALOG_W - PAD * 2 : 260;
    const DETAIL_X = LIST_W + PAD * 2 + 10;
    const DETAIL_W = mobile ? DIALOG_W - PAD * 2 : DIALOG_W - DETAIL_X - PAD;

    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - DIALOG_W / 2, height / 2 - DIALOG_H / 2);

    this.mobile = mobile;
    this.dw = DIALOG_W;
    const dw = DIALOG_W;
    const dh = DIALOG_H;
    this.listW = LIST_W;
    this.detailW = DETAIL_W;
    this.scrollVisibleH = dh - LIST_TOP - 30;
    this.cardNameMaxW = LIST_W - 95;

    this.monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);
    this.mmoState = ServiceLocator.get<MMOStateManager>(Services.MMO_STATE);

    if (initialCategory) {
      this.selectedCategory = initialCategory;
    }
    if (initialComponentId) {
      this.initialComponentId = initialComponentId;
    }
    if (initialShipId) {
      this.initialShipId = initialShipId;
    }

    createDialogChrome(scene, this, dw, dh, {
      title: I18n.t('dialog.components'),
      onClose: () => this.close(),
    });

    // Category row
    this.catContainer = scene.add.container(PAD, 56);
    this.add(this.catContainer);

    // List (left)
    this.listContainer = scene.add.container(PAD, LIST_TOP);
    this.add(this.listContainer);
    this.setupScrollMask();

    // Vertical separator between list and detail (desktop only)
    if (!mobile) {
      const dividerX = LIST_W + PAD + (PAD / 2) + 5;
      const divider = scene.add.graphics();
      divider.lineStyle(1, Colors.cyan, 0.06);
      divider.lineBetween(dividerX, LIST_TOP, dividerX, dh - 30);
      this.add(divider);
    }

    // Detail (right) — on mobile overlays list, hidden initially
    this.detailContainer = scene.add.container(mobile ? PAD : DETAIL_X, LIST_TOP);
    if (mobile) this.detailContainer.setVisible(false);
    this.add(this.detailContainer);
    this.setupDetailScrollMask(mobile ? PAD : DETAIL_X, DETAIL_W);

    // Status
    this.statusText = scene.add.text(dw / 2, dh - 15, '', {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadData();
  }

  private close(): void { closeDialog(this.scene, this); }

  // ============ Mobile Back Button ============

  private createMobileBackBtn(): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(0, 0);
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1e2844, 0.9);
    bg.fillRoundedRect(0, 0, 80, 26, 4);
    bg.lineStyle(1, 0x00ffff, 0.1);
    bg.strokeRoundedRect(0, 0, 80, 26, 4);
    btn.add(bg);
    btn.add(this.scene.add.text(40, 13, '\u2190 Back', {
      fontSize: '12px', color: '#8899bb', fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5));
    const zone = this.scene.add.zone(0, 0, 80, 26).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, 80, 26), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerdown', () => {
      this.listContainer.setVisible(true);
      this.detailContainer.setVisible(false);
    });
    btn.add(zone);
    return btn;
  }

  // ============ Data Loading ============

  private async loadData(): Promise<void> {
    this.setStatus('Loading components...');
    const [comps, owned] = await Promise.all([
      this.monetization.fetchAvailableComponents(),
      this.monetization.fetchPlayerComponents(),
    ]);

    this.components = comps;

    // Build owned map: count, equipped ships, unequipped instances
    this.ownedMap.clear();
    for (const inst of owned) {
      let info = this.ownedMap.get(inst.definitionId);
      if (!info) {
        info = { count: 0, instances: [], equippedShipIds: [], unequippedIds: [] };
        this.ownedMap.set(inst.definitionId, info);
      }
      info.count++;
      info.instances.push(inst);
      if (inst.shipId) {
        info.equippedShipIds.push(inst.shipId);
      } else {
        info.unequippedIds.push(inst.id);
      }
    }

    this.setStatus('');

    // Auto-select a specific component if opened from a required-component prompt
    if (this.initialComponentId) {
      const target = this.components.find(c => c.id === this.initialComponentId);
      if (target) {
        this.selectedCategory = target.category as ComponentCategory;
        this.selectedComponent = target;
      }
      this.initialComponentId = null;
    }

    this.filterAndRender();
  }

  /** Count of components per category (for greying out empty tabs) */
  private categoryCounts: Map<string, number> = new Map();

  private filterAndRender(): void {
    // Build category counts
    this.categoryCounts.clear();
    this.categoryCounts.set('all', this.components.length);
    for (const comp of this.components) {
      this.categoryCounts.set(comp.category, (this.categoryCounts.get(comp.category) || 0) + 1);
    }

    this.filteredComponents = this.selectedCategory === 'all'
      ? [...this.components]
      : this.components.filter(c => c.category === this.selectedCategory);

    // Sort by tier then name
    this.filteredComponents.sort((a, b) => {
      const tierA = TIER_SORT[a.tier] ?? 99;
      const tierB = TIER_SORT[b.tier] ?? 99;
      if (tierA !== tierB) return tierA - tierB;
      return a.name.localeCompare(b.name);
    });

    if (this.selectedComponent) {
      this.selectedComponent = this.filteredComponents.find(c => c.id === this.selectedComponent!.id)
        || this.filteredComponents[0] || null;
    } else {
      this.selectedComponent = this.filteredComponents[0] || null;
    }
    this.detailView = 'info';
    this.scrollY = 0;
    this.renderCategories();
    this.renderList();
    this.renderDetail();
  }

  /** Resolve a ship ID to a human-readable name with variant */
  private getShipLabel(shipId: string): string {
    const unit = this.mmoState.getUnit(shipId);
    if (!unit) return shipId.substring(0, 10) + '\u2026';
    if (unit.customName) return unit.customName;
    if (unit.shipVariant) {
      // Convert variant ID like "sparrow_mk2" to "Sparrow Mk2"
      return unit.shipVariant.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
    return unit.type;
  }

  // ============ Category Tabs ============

  private renderCategories(): void {
    this.catContainer.removeAll(true);
    const totalW = this.dw - PAD * 2;
    const gap = 3;
    const totalGap = gap * (CATEGORIES.length - 1);
    const btnW = Math.floor((totalW - totalGap) / CATEGORIES.length);

    let x = 0;
    for (const cat of CATEGORIES) {
      const isActive = cat.id === this.selectedCategory;
      const count = this.categoryCounts.get(cat.id) || 0;
      const isEmpty = count === 0;
      const btn = this.scene.add.container(x, 0);
      const h = 24;

      const bg = this.scene.add.graphics();
      const drawBg = (active: boolean, hover: boolean) => {
        bg.clear();
        if (isEmpty) {
          bg.fillStyle(0x151525, 0.5);
          bg.fillRoundedRect(0, 0, btnW, h, 3);
          bg.lineStyle(1, 0x222233, 0.2);
          bg.strokeRoundedRect(0, 0, btnW, h, 3);
          return;
        }
        const fill = active ? 0x44aaff : hover ? 0x2a3a5a : 0x1e2844;
        bg.fillStyle(fill, active ? 1 : 0.8);
        bg.fillRoundedRect(0, 0, btnW, h, 3);
        const border = active ? 0x66ccff : hover ? 0x445577 : 0x334466;
        bg.lineStyle(1, border, active ? 0.6 : 0.3);
        bg.strokeRoundedRect(0, 0, btnW, h, 3);
      };
      drawBg(isActive, false);
      btn.add(bg);

      const text = this.scene.add.text(btnW / 2, h / 2, cat.label, {
        fontSize: FontSize.xs,
        color: isEmpty ? '#333344' : isActive ? '#000000' : '#8899bb',
        fontFamily: Fonts.heading,
      });
      text.setOrigin(0.5);
      btn.add(text);

      if (!isEmpty) {
        const hitZone = this.scene.add.zone(0, 0, btnW, h).setOrigin(0, 0);
        hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnW, h), Phaser.Geom.Rectangle.Contains);
        hitZone.on('pointerover', () => {
          if (!isActive) drawBg(false, true);
          this.scene.input.setDefaultCursor('pointer');
        });
        hitZone.on('pointerout', () => {
          drawBg(isActive, false);
          this.scene.input.setDefaultCursor('default');
        });
        hitZone.on('pointerdown', () => {
          this.selectedCategory = cat.id;
          this.filterAndRender();
        });
        btn.add(hitZone);
      }

      this.catContainer.add(btn);
      x += btnW + gap;
    }
  }

  // ============ Component List (left) ============

  private setupScrollMask(): void {
    const wx = this.x + PAD;
    const wy = this.y + LIST_TOP;
    const shape = this.scene.make.graphics({});
    shape.fillRect(wx, wy, this.listW, this.scrollVisibleH);
    this.scrollMask = new Phaser.Display.Masks.GeometryMask(this.scene, shape);
    this.listContainer.setMask(this.scrollMask);

    this.scene.input.on('wheel', (pointer: Phaser.Input.Pointer, _gos: any, _dx: number, dy: number, _dz: number) => {
      if (!this.visible) return;
      const localX = pointer.x - this.x;
      const detailStartX = this.mobile ? 0 : this.listW + PAD * 2 + 10;
      const isOverDetail = this.detailContainer.visible && localX >= detailStartX;
      if (isOverDetail) {
        this.detailScrollY = Phaser.Math.Clamp(this.detailScrollY + dy * 0.3, 0, Math.max(0, this.detailMaxScrollY));
        this.detailContainer.y = LIST_TOP - this.detailScrollY;
      } else if (this.listContainer.visible) {
        this.scrollY = Phaser.Math.Clamp(this.scrollY + dy * 0.3, 0, Math.max(0, this.maxScrollY));
        this.listContainer.y = LIST_TOP - this.scrollY;
      }
    });

    // Touch/pointer-drag scrolling for mobile
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.visible) return;
      const localX = pointer.x - this.x;
      const localY = pointer.y - this.y;
      if (localX >= 0 && localX <= this.dw && localY >= LIST_TOP && localY <= LIST_TOP + this.scrollVisibleH) {
        this.isDraggingScroll = true;
        this.dragStartY = pointer.y;
        this.dragStartScrollY = this.listContainer.visible ? this.scrollY : this.detailScrollY;
      }
    });
    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.isDraggingScroll || !this.visible) return;
      const dy = this.dragStartY - pointer.y;
      const localX = pointer.x - this.x;
      const detailStartX = this.mobile ? 0 : this.listW + PAD * 2 + 10;
      const isOverDetail = this.detailContainer.visible && localX >= detailStartX;
      if (isOverDetail) {
        this.detailScrollY = Phaser.Math.Clamp(this.dragStartScrollY + dy, 0, Math.max(0, this.detailMaxScrollY));
        this.detailContainer.y = LIST_TOP - this.detailScrollY;
      } else if (this.listContainer.visible) {
        this.scrollY = Phaser.Math.Clamp(this.dragStartScrollY + dy, 0, Math.max(0, this.maxScrollY));
        this.listContainer.y = LIST_TOP - this.scrollY;
      }
    });
    this.scene.input.on('pointerup', () => {
      this.isDraggingScroll = false;
    });
  }

  private setupDetailScrollMask(detailX: number, detailW: number): void {
    const wx = this.x + detailX;
    const wy = this.y + LIST_TOP;
    const shape = this.scene.make.graphics({});
    shape.fillRect(wx, wy, detailW, this.scrollVisibleH);
    this.detailScrollMask = new Phaser.Display.Masks.GeometryMask(this.scene, shape);
    this.detailContainer.setMask(this.detailScrollMask);
  }

  private renderList(): void {
    this.listContainer.removeAll(true);
    let y = 0;
    const cardH = 44;

    if (this.filteredComponents.length === 0) {
      const emptyText = this.scene.add.text(this.listW / 2, 40, 'No components\nin this category', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      emptyText.setOrigin(0.5, 0);
      this.listContainer.add(emptyText);
      this.maxScrollY = 0;
      return;
    }

    for (const comp of this.filteredComponents) {
      this.listContainer.add(this.createComponentCard(0, y, comp, cardH));
      y += cardH + 3;
    }

    this.maxScrollY = Math.max(0, y - this.scrollVisibleH);
  }

  private createComponentCard(x: number, y: number, comp: ComponentDefinition, cardH: number): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);
    const isSelected = this.selectedComponent?.id === comp.id;
    const owned = this.ownedMap.get(comp.id);

    const cardBg = this.scene.add.graphics();
    const drawBg = (selected: boolean, hover: boolean) => {
      cardBg.clear();
      const fill = selected ? 0x253a66 : hover ? Colors.bgCardHover : Colors.bgCard;
      cardBg.fillStyle(fill, selected ? 0.9 : 0.8);
      cardBg.fillRoundedRect(0, 0, this.listW, cardH, Radius.sm);
      const bColor = selected ? 0x44aaff : hover ? Colors.cyan : Colors.cyan;
      const bAlpha = selected ? 0.4 : hover ? 0.15 : 0.06;
      cardBg.lineStyle(1, bColor, bAlpha);
      cardBg.strokeRoundedRect(0, 0, this.listW, cardH, Radius.sm);
    };
    drawBg(isSelected, false);
    card.add(cardBg);

    // Tier pill
    const tierLabel = TIER_LABELS[comp.tier] || comp.tier;
    const tierColor = TIER_COLORS[comp.tier] || '#aaaaaa';
    const tierBgColor = TIER_BG[comp.tier] || 0x333344;
    const pillW = 36;
    const pillH = 16;
    const pillG = this.scene.add.graphics();
    pillG.fillStyle(tierBgColor, 0.9);
    pillG.fillRoundedRect(6, (cardH - pillH) / 2, pillW, pillH, 3);
    card.add(pillG);
    const tierText = this.scene.add.text(6 + pillW / 2, cardH / 2, tierLabel, {
      fontSize: '9px', color: tierColor, fontFamily: Fonts.heading,
    });
    tierText.setOrigin(0.5);
    card.add(tierText);

    // Component name — cropped to avoid overflow
    const nameText = this.scene.add.text(50, 7, comp.name, {
      fontSize: FontSize.xs, color: '#ccddff', fontFamily: Fonts.heading,
    });
    if (nameText.width > this.cardNameMaxW) {
      nameText.setCrop(0, 0, this.cardNameMaxW, nameText.height);
    }
    card.add(nameText);

    // Category
    const catText = this.scene.add.text(50, 24, comp.category.toUpperCase(), {
      fontSize: '9px', color: '#667788', fontFamily: Fonts.body,
    });
    card.add(catText);

    // Price (right, top)
    const priceStr = `$${comp.priceUsd}`;
    const priceText = this.scene.add.text(this.listW - 8, 7, priceStr, {
      fontSize: FontSize.xs, color: CSSColors.gold, fontFamily: Fonts.heading,
    });
    priceText.setOrigin(1, 0);
    card.add(priceText);

    // Owned / Equipped badges (right, bottom)
    if (owned) {
      const equippedCount = owned.equippedShipIds.length;
      const unequippedCount = owned.unequippedIds.length;
      const parts: string[] = [];
      if (equippedCount > 0) parts.push(`${equippedCount} EQP`);
      if (unequippedCount > 0) parts.push(`${unequippedCount} INV`);
      const badgeLabel = parts.join(' \u00b7 ');

      const ownedText = this.scene.add.text(this.listW - 8, 24, badgeLabel, {
        fontSize: '9px', color: equippedCount > 0 ? '#44cc44' : '#ccaa44', fontFamily: Fonts.heading,
      });
      ownedText.setOrigin(1, 0);
      card.add(ownedText);
    }

    // Interactive zone
    const hitZone = this.scene.add.zone(0, 0, this.listW, cardH).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.listW, cardH), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      if (!isSelected) drawBg(false, true);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      drawBg(isSelected, false);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', () => {
      this.selectedComponent = comp;
      this.detailView = 'info';
      this.renderList();
      this.renderDetail();
      if (this.mobile) {
        this.listContainer.setVisible(false);
        this.detailContainer.setVisible(true);
      }
    });
    card.add(hitZone);

    return card;
  }

  // ============ Detail Panel (right) ============

  private renderDetail(): void {
    this.detailContainer.removeAll(true);
    this.detailScrollY = 0;
    this.detailContainer.y = LIST_TOP;

    if (!this.selectedComponent) {
      const hint = this.scene.add.text(this.detailW / 2, 80, 'Select a component\nto view details', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      hint.setOrigin(0.5, 0);
      this.detailContainer.add(hint);
      return;
    }

    switch (this.detailView) {
      case 'info': this.renderInfoView(); break;
      case 'buy-confirm': this.renderBuyConfirm(); break;
      case 'equip-confirm': this.renderEquipConfirm(); break;
    }
  }

  private renderInfoView(): void {
    const comp = this.selectedComponent!;
    const owned = this.ownedMap.get(comp.id);
    let y = 0;

    // Mobile back button
    if (this.mobile) {
      const backBtn = this.createMobileBackBtn();
      this.detailContainer.add(backBtn);
      y += 30;
    }

    // Name
    this.detailContainer.add(this.scene.add.text(0, y, comp.name, {
      fontSize: FontSize.lg, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    }));
    y += 24;

    // Tier + Category + tier description
    const tierLabel = TIER_LABELS[comp.tier] || comp.tier;
    const tierColor = TIER_COLORS[comp.tier] || '#aaaaaa';
    const tierDesc = TIER_DESCRIPTIONS[comp.tier] || '';
    this.detailContainer.add(this.scene.add.text(0, y, `${tierLabel}  \u00b7  ${comp.category.toUpperCase()}  \u00b7  ${tierDesc}`, {
      fontSize: FontSize.xs, color: tierColor, fontFamily: Fonts.heading,
    }));
    y += 18;

    // Description
    if (comp.description) {
      const descText = this.scene.add.text(0, y, comp.description, {
        fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
        wordWrap: { width: this.detailW },
      });
      this.detailContainer.add(descText);
      y += descText.height + 10;
    }

    this.addDivider(y); y += 8;

    // Stat Bonuses (with dotted leaders)
    const statEntries = Object.entries(comp.stats);
    if (statEntries.length > 0) {
      this.detailContainer.add(this.scene.add.text(0, y, 'STAT BONUSES', {
        fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
      }));
      y += 16;

      for (const [stat, value] of statEntries) {
        const sign = value >= 0 ? '+' : '';
        const color = value >= 0 ? '#55cc55' : '#ff5555';
        const label = STAT_LABELS[stat] || stat;
        y = this.addStatRow(0, y, label, `${sign}${value}`, color);
      }
      y += 4;
    }

    this.addDivider(y); y += 8;

    // Compatible ships
    if (comp.compatibleShipTypes.length > 0) {
      this.detailContainer.add(this.scene.add.text(0, y, 'COMPATIBLE SHIPS', {
        fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
      }));
      y += 16;

      // Render each ship type — green if player owns one, muted if not. Same font for alignment.
      const playerTypes = new Set(this.mmoState.getPlayerUnits().map(u => u.type.toLowerCase()));
      let sx = 0;
      for (let i = 0; i < comp.compatibleShipTypes.length; i++) {
        const rawType = comp.compatibleShipTypes[i];
        const displayName = rawType.charAt(0).toUpperCase() + rawType.slice(1);
        const hasShip = playerTypes.has(rawType.toLowerCase());
        const suffix = i < comp.compatibleShipTypes.length - 1 ? ',  ' : '';

        const shipText = this.scene.add.text(sx, y, displayName + suffix, {
          fontSize: FontSize.xs,
          color: hasShip ? '#44ee66' : '#667788',
          fontFamily: Fonts.body,
        });
        this.detailContainer.add(shipText);
        sx += shipText.width;
      }
      y += 18;

      this.addDivider(y); y += 8;
    }

    // Ownership section
    if (owned && owned.count > 0) {
      this.detailContainer.add(this.scene.add.text(0, y, 'YOUR INVENTORY', {
        fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
      }));
      y += 16;

      const equippedCount = owned.equippedShipIds.length;
      const unequippedCount = owned.unequippedIds.length;
      this.detailContainer.add(this.scene.add.text(0, y, `Owned: ${owned.count}  \u00b7  Equipped: ${equippedCount}  \u00b7  Inventory: ${unequippedCount}`, {
        fontSize: FontSize.xs, color: '#44cc44', fontFamily: Fonts.body,
      }));
      y += 16;

      // Show where each is equipped (with star rating, enhance, and unequip buttons)
      const RARITY_CSS_MAP: Record<string, string> = {
        common: '#cccccc', uncommon: '#4ade80', rare: '#60a5fa', epic: '#a855f7', legendary: '#FFD700',
      };
      for (const inst of owned.instances) {
        if (inst.shipId) {
          const shipLabel = this.getShipLabel(inst.shipId);
          const stars = inst.starRating || 1;
          const starStr = '\u2605'.repeat(stars) + '\u2606'.repeat(5 - stars);
          const rarityColor = RARITY_CSS_MAP[inst.rarity || 'common'] || '#cccccc';

          this.detailContainer.add(this.scene.add.text(0, y, `${starStr}  ${shipLabel}`, {
            fontSize: FontSize.xs, color: rarityColor, fontFamily: Fonts.body,
          }));

          // Unequip button
          const isUnequipping = this.pendingComponentActionId === inst.id;
          const unequipBtn = this.scene.add.text(this.detailW - 8, y, isUnequipping ? 'Unequipping...' : 'Unequip', {
            fontSize: FontSize.xs,
            color: isUnequipping ? '#8899aa' : '#ff8844',
            fontFamily: Fonts.heading,
            backgroundColor: isUnequipping ? '#222833' : '#221100',
            padding: { x: 4, y: 2 },
          });
          unequipBtn.setOrigin(1, 0);
          const capturedInstId = inst.id;
          if (!this.pendingComponentActionId) {
            unequipBtn.setInteractive({ useHandCursor: true });
            unequipBtn.on('pointerdown', () => this.handleUnequip(capturedInstId, comp.name));
          }
          this.detailContainer.add(unequipBtn);

          // Enhance button
          if (stars < 5) {
            const enhBtnX = this.detailW - 8 - unequipBtn.width - 8;
            const enhBtn = this.scene.add.text(enhBtnX, y, '\u2b06 Enhance', {
              fontSize: FontSize.xs, color: CSSColors.cyan, fontFamily: Fonts.heading,
              backgroundColor: '#112233', padding: { x: 4, y: 2 },
            });
            enhBtn.setOrigin(1, 0);
            enhBtn.setInteractive({ useHandCursor: true });
            enhBtn.on('pointerdown', () => {
              if (!this.enhancementDialog) {
                this.enhancementDialog = new ComponentEnhancementDialog(this.scene);
              }
              this.enhancementDialog.show(inst.id, comp.name, comp.category);
            });
            this.detailContainer.add(enhBtn);
          }

          y += 16;
        }
      }

      // Show unequipped items
      if (unequippedCount > 0) {
        this.detailContainer.add(this.scene.add.text(0, y, `${unequippedCount} in inventory (unequipped)`, {
          fontSize: FontSize.xs, color: '#ccaa44', fontFamily: Fonts.body,
        }));
        y += 14;
      }

      y += 6;
      this.addDivider(y); y += 8;
    }

    // Price
    this.detailContainer.add(this.scene.add.text(this.detailW / 2, y, `$${comp.priceUsd} USD`, {
      fontSize: FontSize.xl, color: CSSColors.gold, fontFamily: Fonts.heading,
    }).setOrigin(0.5, 0));
    y += 30;

    // Buy & Equip button
    this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, 'Buy & Equip', 0x224422, () => {
      this.detailView = 'buy-confirm';
      if (!this.selectedShipId) this.selectedShipId = this.initialShipId;
      this.renderDetail();
    }));
    y += 40;

    // Equip button (if unequipped instances exist)
    if (owned && owned.unequippedIds.length > 0) {
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, 'Equip Owned', 0x223366, () => {
        this.detailView = 'equip-confirm';
        if (!this.selectedShipId) this.selectedShipId = this.initialShipId;
        this.renderDetail();
      }));
      y += 40;
    }

    // Set detail scroll bounds
    this.detailMaxScrollY = Math.max(0, y - this.scrollVisibleH);
  }

  // ============ Buy Confirmation Sub-view ============

  private renderBuyConfirm(): void {
    this.renderShipPickerView('buy');
  }

  // ============ Equip Confirmation Sub-view ============

  private renderEquipConfirm(): void {
    this.renderShipPickerView('equip');
  }

  /**
   * Shared ship picker for both buy and equip flows.
   * mode = 'buy': purchase + equip. mode = 'equip': equip an owned unequipped instance.
   */
  private renderShipPickerView(mode: 'buy' | 'equip'): void {
    const comp = this.selectedComponent!;
    let y = 0;

    // Mobile back button
    if (this.mobile) {
      const backBtn = this.createMobileBackBtn();
      this.detailContainer.add(backBtn);
      y += 30;
    }

    // Back button
    this.detailContainer.add(this.createActionBtn(0, y, 90, 28, '\u2190 Back', 0x1a2a44, () => {
      this.detailView = 'info';
      this.renderDetail();
    }));
    y += 38;

    // Title
    const title = mode === 'buy' ? `Buy & equip ${comp.name}:` : `Equip ${comp.name} to:`;
    this.detailContainer.add(this.scene.add.text(0, y, title, {
      fontSize: FontSize.sm, color: CSSColors.textWhite, fontFamily: Fonts.heading,
      wordWrap: { width: this.detailW },
    }));
    y += 22;

    // Get compatible player ships
    const playerUnits = this.mmoState.getPlayerUnits();
    const compatTypes = comp.compatibleShipTypes.map(s => s.toLowerCase());
    const compatibleShips = playerUnits.filter(u => {
      if (compatTypes.length === 0) return true;
      return compatTypes.includes(u.type.toLowerCase());
    }).sort((a, b) => {
      // Stable order so the same ship appears in the same row across components
      const an = (a as any).customName || (a as any).shipVariant || a.type;
      const bn = (b as any).customName || (b as any).shipVariant || b.type;
      const cmp = String(an).localeCompare(String(bn));
      return cmp !== 0 ? cmp : String(a.id).localeCompare(String(b.id));
    });

    if (compatibleShips.length === 0) {
      this.detailContainer.add(this.scene.add.text(0, y, 'No compatible ships available.', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, lineSpacing: 4,
      }));
      y += 36;

      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, 'Buy a Ship', 0x224488, () => {
        this.close();
        new ShipStoreDialog(this.scene);
      }));
      return;
    }

    // Ship cards
    for (const ship of compatibleShips) {
      const isSelected = ship.id === this.selectedShipId;
      const card = this.scene.add.container(0, y);
      const cardH = 38;

      const cardBg = this.scene.add.graphics();
      const drawShipBg = (selected: boolean, hover: boolean) => {
        cardBg.clear();
        const fill = selected ? 0x253a66 : hover ? Colors.bgCardHover : Colors.bgCard;
        cardBg.fillStyle(fill, selected ? 0.9 : 0.8);
        cardBg.fillRoundedRect(0, 0, this.detailW, cardH, Radius.sm);
        const bAlpha = selected ? 0.4 : hover ? 0.15 : 0.06;
        cardBg.lineStyle(1, selected ? 0x44aaff : Colors.cyan, bAlpha);
        cardBg.strokeRoundedRect(0, 0, this.detailW, cardH, Radius.sm);
      };
      drawShipBg(isSelected, false);
      card.add(cardBg);

      const shipLabel = ship.customName
        ? ship.customName
        : ship.shipVariant
          ? ship.shipVariant.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
          : ship.type;
      const locLabel = ship.dockedAtPlanetId ? 'Docked' : `(${ship.position.q}, ${ship.position.r})`;
      card.add(this.scene.add.text(10, 4, shipLabel, {
        fontSize: FontSize.sm, color: CSSColors.textWhite, fontFamily: Fonts.heading,
      }));
      card.add(this.scene.add.text(10, 18, locLabel, {
        fontSize: '9px', color: '#667788', fontFamily: Fonts.body,
      }));

      const hitZone = this.scene.add.zone(0, 0, this.detailW, cardH).setOrigin(0, 0);
      hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.detailW, cardH), Phaser.Geom.Rectangle.Contains);
      hitZone.on('pointerover', () => {
        if (!isSelected) drawShipBg(false, true);
        this.scene.input.setDefaultCursor('pointer');
      });
      hitZone.on('pointerout', () => {
        drawShipBg(isSelected, false);
        this.scene.input.setDefaultCursor('default');
      });
      hitZone.on('pointerdown', () => {
        this.selectedShipId = ship.id;
        this.renderDetail();
      });
      card.add(hitZone);

      this.detailContainer.add(card);
      y += cardH + 4;
    }

    // If a ship is selected, show before/after stat comparison + confirm
    if (this.selectedShipId) {
      const ship = compatibleShips.find(s => s.id === this.selectedShipId);
      if (ship) {
        y += 4;
        this.addDivider(y); y += 8;

        this.detailContainer.add(this.scene.add.text(0, y, 'STAT PREVIEW', {
          fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
        }));
        y += 16;

        // Column headers
        const colBefore = this.detailW - 90;
        const colAfter = this.detailW - 30;

        this.detailContainer.add(this.scene.add.text(colBefore, y, 'Now', {
          fontSize: '9px', color: '#667788', fontFamily: Fonts.heading,
        }).setOrigin(0.5, 0));
        this.detailContainer.add(this.scene.add.text(colAfter, y, 'After', {
          fontSize: '9px', color: '#667788', fontFamily: Fonts.heading,
        }).setOrigin(0.5, 0));
        y += 14;

        for (const [stat, value] of Object.entries(comp.stats)) {
          const unitKey = STAT_TO_UNIT[stat];
          const label = STAT_LABELS[stat] || stat;

          if (unitKey) {
            const currentVal = (ship[unitKey] as number) || 0;
            const newVal = currentVal + value;
            const improved = newVal > currentVal;

            this.detailContainer.add(this.scene.add.text(0, y, label, {
              fontSize: FontSize.xs, color: '#aabbcc', fontFamily: Fonts.body,
            }));

            this.detailContainer.add(this.scene.add.text(colBefore, y, `${currentVal}`, {
              fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.heading,
            }).setOrigin(0.5, 0));

            this.detailContainer.add(this.scene.add.text(colAfter, y, `${newVal}`, {
              fontSize: FontSize.xs, color: improved ? '#44ff66' : '#889999', fontFamily: Fonts.heading,
            }).setOrigin(0.5, 0));

            if (improved) {
              this.detailContainer.add(this.scene.add.text(colBefore + 22, y, '\u2192', {
                fontSize: FontSize.xs, color: '#44ff66', fontFamily: Fonts.body,
              }));
            }
          } else {
            this.detailContainer.add(this.scene.add.text(0, y, label, {
              fontSize: FontSize.xs, color: '#aabbcc', fontFamily: Fonts.body,
            }));
            const sign = value >= 0 ? '+' : '';
            this.detailContainer.add(this.scene.add.text(colAfter, y, `${sign}${value}`, {
              fontSize: FontSize.xs, color: value >= 0 ? '#44ff66' : '#ff5555', fontFamily: Fonts.heading,
            }).setOrigin(0.5, 0));
          }
          y += 18;
        }

        y += 8;

        if (mode === 'buy') {
          // Price + Confirm purchase
          this.detailContainer.add(this.scene.add.text(this.detailW / 2, y, `$${comp.priceUsd} USD`, {
            fontSize: FontSize.lg, color: CSSColors.gold, fontFamily: Fonts.heading,
          }).setOrigin(0.5, 0));
          y += 28;

          // Payment method selection
          this.paymentPanel = new PaymentSelectionPanel(this.scene, 0, y, this.detailW);
          this.detailContainer.add(this.paymentPanel);
          y += this.paymentPanel.getHeight() + 8;

          const btnLabel = this.isPurchasing ? 'PROCESSING...' : `Confirm \u2014 $${comp.priceUsd}`;
          const btnColor = this.isPurchasing ? 0x333333 : 0x225522;
          this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 36, btnLabel, btnColor, () => {
            if (!this.isPurchasing) this.handlePurchase();
          }));
          y += 44;
        } else {
          // Equip confirm (no cost)
          const shipLabel = ship.customName
            ? ship.customName
            : ship.shipVariant
              ? ship.shipVariant.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
              : ship.type;
          const owned = this.ownedMap.get(comp.id);
          const pendingEquipId = owned?.unequippedIds[0] || null;
          const isEquipping = !!pendingEquipId && this.pendingComponentActionId === pendingEquipId;
          this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 36, isEquipping ? 'Equipping...' : `Equip to ${shipLabel}`, isEquipping ? 0x333333 : 0x223366, () => {
            this.handleEquip(ship);
          }));
          y += 44;
        }
      }
    }

    // Set detail scroll bounds (add padding so last button isn't cut off)
    this.detailMaxScrollY = Math.max(0, y + 10 - this.scrollVisibleH);
  }

  // ============ Actions ============

  private async handlePurchase(): Promise<void> {
    if (!this.selectedComponent || !this.selectedShipId || this.isPurchasing) return;

    this.isPurchasing = true;
    this.setStatus(`Purchasing ${this.selectedComponent.name}...`);
    this.renderDetail();

    const paymentFlow = ServiceLocator.tryGet<PaymentFlowManager>(Services.PAYMENT_FLOW);
    if (!paymentFlow) {
      this.setStatus('Payment system not available');
      this.isPurchasing = false;
      return;
    }

    const chain = this.paymentPanel?.getSelectedChain() || 'solana';
    const stablecoin = this.paymentPanel?.getSelectedStablecoin() || 'USDC';

    const result = await paymentFlow.executePayment({
      assetType: 'component',
      amountUsd: this.selectedComponent.priceUsd,
      chain,
      stablecoin,
      metadata: {
        componentDefId: this.selectedComponent.id,
        shipId: this.selectedShipId,
      },
    });

    this.isPurchasing = false;

    if (result.success) {
      this.showSuccessPopup(this.selectedComponent.name);
      this.detailView = 'info';
      await this.loadData();
      EventBus.emit('components_changed');
    } else {
      this.setStatus(`Failed: ${result.error}`);
      this.renderDetail();
    }
  }

  private async handleUnequip(instanceId: string, compName: string): Promise<void> {
    if (this.pendingComponentActionId) {
      this.setStatus('Please wait for the current component action to finish.');
      return;
    }
    this.pendingComponentActionId = instanceId;
    this.setStatus(`Unequipping ${compName}...`);
    this.renderDetail();
    const result = await this.monetization.unequipComponent(instanceId);
    if (result.success) {
      this.setStatus(`${compName} unequipped`);
      this.detailView = 'info';
      await this.loadData();
      EventBus.emit('components_changed');
      this.scene.time.delayedCall(2500, () => {
        if (this.scene) this.setStatus('');
      });
    } else {
      this.pendingComponentActionId = null;
      this.setStatus(`Failed: ${result.error}`);
      this.renderDetail();
      this.scene.time.delayedCall(3000, () => {
        if (this.scene) this.setStatus('');
      });
    }
    this.pendingComponentActionId = null;
  }

  private async handleEquip(ship: MMOUnit): Promise<void> {
    if (this.pendingComponentActionId) {
      this.setStatus('Please wait for the current component action to finish.');
      return;
    }
    if (!this.selectedComponent) return;
    const owned = this.ownedMap.get(this.selectedComponent.id);
    if (!owned || owned.unequippedIds.length === 0) return;

    const instanceId = owned.unequippedIds[0];
    this.pendingComponentActionId = instanceId;
    this.setStatus(`Equipping ${this.selectedComponent.name}...`);
    this.renderDetail();

    const result = await this.monetization.equipComponent(instanceId, ship.id);

    if (result.success) {
      this.showSuccessPopup(this.selectedComponent.name);
      this.detailView = 'info';
      await this.loadData();
      EventBus.emit('components_changed');
    } else {
      this.pendingComponentActionId = null;
      this.setStatus(`Failed: ${result.error}`);
      this.renderDetail();
      this.scene.time.delayedCall(3000, () => {
        if (this.scene) this.setStatus('');
      });
    }
    this.pendingComponentActionId = null;
  }

  // ============ Helpers ============

  private addDivider(y: number): void {
    const g = this.scene.add.graphics();
    g.lineStyle(1, Colors.cyan, 0.08);
    g.lineBetween(0, y, this.detailW, y);
    this.detailContainer.add(g);
  }

  /** Stat row with dotted leader line and hover highlight */
  private addStatRow(x: number, y: number, label: string, value: string, valueColor: string): number {
    const ROW_H = 22;
    const ROW_PAD = 4;
    const row = this.scene.add.container(x, y);

    const hoverBg = this.scene.add.graphics();
    row.add(hoverBg);

    const lbl = this.scene.add.text(ROW_PAD, 3, label, {
      fontSize: FontSize.xs, color: '#aabbcc', fontFamily: Fonts.body,
    });
    row.add(lbl);

    const val = this.scene.add.text(this.detailW - ROW_PAD, 3, value, {
      fontSize: FontSize.xs, color: valueColor, fontFamily: Fonts.heading,
    });
    val.setOrigin(1, 0);
    row.add(val);

    // Dotted leader
    const dotsG = this.scene.add.graphics();
    const dotsY = 11;
    const dotStart = lbl.x + lbl.width + 6;
    const dotEnd = this.detailW - ROW_PAD - val.width - 6;
    dotsG.fillStyle(0x445566, 0.4);
    for (let dx = dotStart; dx < dotEnd; dx += 5) {
      dotsG.fillCircle(dx, dotsY, 1);
    }
    row.add(dotsG);

    // Hover zone
    const hitZone = this.scene.add.zone(0, 0, this.detailW, ROW_H).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.detailW, ROW_H), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      hoverBg.clear();
      hoverBg.fillStyle(0x44aaff, 0.08);
      hoverBg.fillRoundedRect(0, 0, this.detailW, ROW_H, 3);
      lbl.setColor('#ffffff');
    });
    hitZone.on('pointerout', () => {
      hoverBg.clear();
      lbl.setColor('#aabbcc');
    });
    row.add(hitZone);

    this.detailContainer.add(row);
    return y + ROW_H;
  }

  private createActionBtn(
    x: number, y: number, w: number, h: number,
    label: string, color: number, callback: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 0.8);
    bg.fillRoundedRect(0, 0, w, h, Radius.sm);
    bg.lineStyle(1, Colors.cyan, 0.2);
    bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    btn.add(bg);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.sm, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    const hitZone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(color + 0x222222, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.3);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(color, 0.8);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.2);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', callback);
    btn.add(hitZone);

    return btn;
  }

  private setStatus(message: string): void {
    this.statusText.setText(message);
  }

  private showSuccessPopup(componentName: string): void {
    const popup = this.scene.add.container(this.dw / 2, 0);
    popup.setDepth(9999);
    this.add(popup);

    // Background
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x0a1e0a, 0.95);
    bg.fillRoundedRect(-140, -60, 280, 120, 8);
    bg.lineStyle(2, 0x44ff66, 0.6);
    bg.strokeRoundedRect(-140, -60, 280, 120, 8);
    popup.add(bg);

    // Checkmark
    popup.add(this.scene.add.text(0, -40, '\u2713', {
      fontSize: '28px', color: '#44ff66', fontFamily: '"Share Tech Mono", monospace',
    }).setOrigin(0.5));

    // Title
    popup.add(this.scene.add.text(0, -10, 'Component Installed!', {
      fontSize: '14px', color: '#44ff66', fontFamily: '"Share Tech Mono", monospace',
    }).setOrigin(0.5));

    // Component name
    popup.add(this.scene.add.text(0, 10, componentName, {
      fontSize: '12px', color: '#ccddee', fontFamily: '"Share Tech Mono", monospace',
    }).setOrigin(0.5));

    popup.add(this.scene.add.text(0, 26, 'has been equipped to your ship.', {
      fontSize: '11px', color: '#889aaa', fontFamily: '"Share Tech Mono", monospace',
    }).setOrigin(0.5));

    // OK button
    const btnBg = this.scene.add.graphics();
    btnBg.fillStyle(0x224422, 1);
    btnBg.fillRoundedRect(-40, 40, 80, 28, 4);
    btnBg.lineStyle(1, 0x44ff66, 0.3);
    btnBg.strokeRoundedRect(-40, 40, 80, 28, 4);
    popup.add(btnBg);

    const btnText = this.scene.add.text(0, 54, 'OK', {
      fontSize: '13px', color: '#44ff66', fontFamily: '"Share Tech Mono", monospace',
    }).setOrigin(0.5);
    popup.add(btnText);

    const btnZone = this.scene.add.zone(0, 54, 80, 28).setInteractive();
    btnZone.on('pointerover', () => { btnText.setColor('#88ffaa'); this.scene.input.setDefaultCursor('pointer'); });
    btnZone.on('pointerout', () => { btnText.setColor('#44ff66'); this.scene.input.setDefaultCursor('default'); });
    btnZone.on('pointerdown', () => { popup.destroy(); this.scene.input.setDefaultCursor('default'); });
    popup.add(btnZone);

    // Center vertically in dialog
    const scrollH = this.scrollVisibleH;
    popup.setY(LIST_TOP + scrollH / 2);
  }
}
